import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { bulkLogoUpload } from '../../../services/bulkLogoUploadService';

const VALID_IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

const BulkLogoUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const resetState = () => { setFiles([]); setProgress(null); setResult(null); setFatalError(null); };
  const handleClose = () => { if (uploading) return; resetState(); onClose?.(); };

  const filterImageFiles = (fileList) => {
    return Array.from(fileList)?.filter((f) => {
      const ext = f?.name?.split('.')?.pop()?.toLowerCase();
      return VALID_IMAGE_EXTS?.includes(ext);
    });
  };

  const handleFilesSelect = (selectedFiles) => {
    if (!selectedFiles || selectedFiles?.length === 0) return;
    const imageFiles = filterImageFiles(selectedFiles);
    if (imageFiles?.length === 0) {
      setFatalError('No valid image files found. Supported formats: JPG, PNG, GIF, WebP, SVG');
      return;
    }
    setFiles(imageFiles);
    setFatalError(null);
    setResult(null);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    handleFilesSelect(e?.dataTransfer?.files);
  };

  const handleUpload = async () => {
    if (!files?.length) return;
    setUploading(true);
    setResult(null);
    setFatalError(null);
    setProgress({ phase: 'uploading', rowIndex: 0, totalRows: files?.length });
    try {
      const uploadResult = await bulkLogoUpload(files, { onProgress: (p) => setProgress(p) });
      setResult(uploadResult);
      if (uploadResult?.summary?.totalRowsUploaded > 0) onSuccess?.();
    } catch (err) {
      setFatalError(err?.message || 'An unexpected error occurred');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const progressLabel = () => {
    if (!progress) return '';
    if (progress?.phase === 'uploading') {
      if (progress?.filename) return `Uploading "${progress?.filename}" — ${progress?.rowIndex}/${progress?.totalRows}`;
      return `Uploading ${progress?.rowIndex}/${progress?.totalRows}`;
    }
    return 'Processing…';
  };

  const progressPercent = () => {
    if (!progress || !progress?.totalRows) return 5;
    return Math.min(99, Math.round((progress?.rowIndex / progress?.totalRows) * 100));
  };

  const { summary, results } = result || {};

  const confirmationBanner = () => {
    if (!summary) return null;
    const { totalRowsUploaded, totalRowsFailed, totalRowsSkipped, totalRows } = summary;
    if (totalRowsUploaded === totalRows) {
      return (
        <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/40 rounded-lg">
          <Icon name="CheckCircle2" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-success">Upload Complete!</p>
            <p className="text-sm text-success/80 mt-0.5">{totalRowsUploaded} logo{totalRowsUploaded !== 1 ? 's' : ''} uploaded successfully.</p>
          </div>
        </div>
      );
    }
    if (totalRowsUploaded > 0) {
      return (
        <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/40 rounded-lg">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-warning">Upload Completed with Issues</p>
            <p className="text-sm text-warning/80 mt-0.5">
              {totalRowsUploaded} uploaded · {totalRowsFailed} failed · {totalRowsSkipped} not found
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/40 rounded-lg">
        <Icon name="XCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-error">Upload Failed</p>
          <p className="text-sm text-error/80 mt-0.5">No logos were uploaded. Check the errors below.</p>
        </div>
      </div>
    );
  };

  const statusColor = (status) => {
    if (status === 'success') return 'bg-success';
    if (status === 'not_found') return 'bg-warning';
    return 'bg-error';
  };

  const statusLabel = (status) => {
    if (status === 'success') return 'Success';
    if (status === 'not_found') return 'Not Found';
    return 'Failed';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Image" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Bulk Institute Logo Upload</h2>
              <p className="text-xs text-muted-foreground">Upload logos for multiple institutes — matched by Name + City</p>
            </div>
          </div>
          <button onClick={handleClose} disabled={uploading} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {!result && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={16} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 space-y-1">
                  <p className="font-semibold">How it works</p>
                  <ul className="text-blue-600 space-y-1 list-disc list-inside">
                    <li>Select multiple image files at once (JPG, PNG, GIF, WebP, SVG)</li>
                    <li>Each filename must follow: <strong>InstituteName_City.ext</strong></li>
                    <li>Example: <code className="bg-blue-100 px-1 rounded">Acharya Business School_Bangalore.png</code></li>
                    <li>Institute is matched by name + city — use the exact name and city from the system</li>
                    <li>Logos are uploaded to storage and the institute profile is updated automatically</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {!result && (
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground mb-2">Filename Format</p>
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-mono">InstituteName_City.ext</span>
              </div>
              <p className="text-xs text-muted-foreground">Use underscore (_) to separate institute name from city. Spaces are allowed within the name and city.</p>
              <p className="text-xs text-muted-foreground mt-1">Supported formats: {VALID_IMAGE_EXTS?.join(', ')}</p>
            </div>
          )}

          {/* File drop zone */}
          {!result && (
            <div
              onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef?.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/20'}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                multiple
                className="hidden"
                onChange={(e) => handleFilesSelect(e?.target?.files)}
              />
              <Icon name="Images" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              {files?.length > 0 ? (
                <div>
                  <p className="font-medium text-foreground">{files?.length} image{files?.length !== 1 ? 's' : ''} selected</p>
                  <p className="text-sm text-muted-foreground mt-1">Click to change selection</p>
                  <div className="mt-3 max-h-28 overflow-y-auto space-y-1">
                    {files?.slice(0, 8)?.map((f, idx) => (
                      <p key={idx} className="text-xs text-muted-foreground truncate">{f?.name}</p>
                    ))}
                    {files?.length > 8 && (
                      <p className="text-xs text-muted-foreground">…and {files?.length - 8} more</p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-foreground">Drop image files here or click to browse</p>
                  <p className="text-sm text-muted-foreground mt-1">Select multiple files at once · JPG, PNG, GIF, WebP, SVG</p>
                </div>
              )}
            </div>
          )}

          {/* Fatal error */}
          {fatalError && (
            <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/30 rounded-lg">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{fatalError}</p>
            </div>
          )}

          {/* Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{progressLabel()}</span>
                <span className="font-medium text-foreground">{progressPercent()}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${progressPercent()}%` }} />
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {confirmationBanner()}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-success/10 border border-success/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-success">{summary?.totalRowsUploaded}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Uploaded</p>
                </div>
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-warning">{summary?.totalRowsSkipped}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Not Found</p>
                </div>
                <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-error">{summary?.totalRowsFailed}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Failed</p>
                </div>
              </div>

              {results?.filter((r) => r?.status !== 'success')?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Issues ({results?.filter((r) => r?.status !== 'success')?.length})</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {results?.filter((r) => r?.status !== 'success')?.map((r, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2.5 bg-muted/30 rounded-lg">
                        <span className={`flex-shrink-0 mt-0.5 w-2 h-2 rounded-full ${statusColor(r?.status)}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-foreground truncate">{r?.filename}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${r?.status === 'not_found' ? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'}`}>
                              {statusLabel(r?.status)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{r?.error}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results?.filter((r) => r?.status === 'success')?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Successfully Uploaded ({results?.filter((r) => r?.status === 'success')?.length})</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {results?.filter((r) => r?.status === 'success')?.map((r, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2.5 bg-success/5 border border-success/20 rounded-lg">
                        <Icon name="CheckCircle2" size={14} color="var(--color-success)" className="flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-foreground truncate">{r?.instituteName}, {r?.city}</p>
                          <p className="text-xs text-muted-foreground truncate">{r?.filename}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0 bg-muted/20">
          {result ? (
            <Button variant="outline" onClick={resetState}>Upload More</Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              {files?.length > 0 ? `${files?.length} file${files?.length !== 1 ? 's' : ''} ready` : 'No files selected'}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleClose} disabled={uploading}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                variant="default"
                onClick={handleUpload}
                disabled={!files?.length || uploading}
                iconName={uploading ? 'Loader2' : 'Upload'}
              >
                {uploading ? 'Uploading…' : `Upload ${files?.length > 0 ? `${files?.length} Logo${files?.length !== 1 ? 's' : ''}` : 'Logos'}`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkLogoUploadModal;
